import { RoundCreator } from '../../common/phaser/model/round-creator';
import {
  BlackjackEvents,
  BlackjackGameLogic,
  BlackjackLogicAction,
  EventQueue,
  BlackjackGameBuilder,
  BlackjackListener,
  ActionBus,
  BlackjackDealer,
} from 'game-logic';
import { BlackjackRemoteRoundParams } from './blackjack-remote-round-params';
import { BlackjackLocalRoundParams } from './blackjack-local-round-params';
import { RoundParams } from '../../common/phaser/model/round-params';
import { GameRound } from '../../common/phaser/model/game-round';
import { BlackjackGameRound } from './blackjack-game-round';
import { Subject } from 'rxjs';

export class BlackjackRoundCreator extends RoundCreator {
  static readonly defaultLogicVersion = 2;

  public get currentRound(): BlackjackGameRound | undefined {
    return this.gameRound;
  }
  public get blackjackLogicActionBus$() {
    return this.blackjackLogicActionBus.asObservable();
  }
  public get logicEventQueue$() {
    return this.logicEventQueue.asObservable();
  }

  private gameListener: BlackjackListener;
  private gameRound: BlackjackGameRound | undefined;
  private blackjackLogicActionBus = new Subject<ActionBus<BlackjackLogicAction> | null>();
  private logicEventQueue = new Subject<EventQueue<BlackjackEvents> | null>();

  constructor(gameListener: BlackjackListener) {
    super();

    this.gameListener = gameListener;
  }

  public override startNewRound(roundParams: RoundParams): GameRound {
    this.destroyCurrentGame();

    let nextRound: BlackjackGameRound;
    if (BlackjackRemoteRoundParams.isRound(roundParams)) {
      nextRound = this.createRemoteRound(roundParams);
    } else if (BlackjackLocalRoundParams.isRound(roundParams)) {
      nextRound = this.createLocalRound(roundParams);
    } else {
      throw new Error('Failed to create new round');
    }

    nextRound.onGameStateChangedAction = (state) => {
      this.gameListener.onGameStateChange(state);
    };
    nextRound.gameLogicActions.on('onMoveExecuted', (payload) => {
      this.gameListener.onNewMove(payload.moves);
    });
    nextRound.gameLogicActions.on('onScoreChanged', (payload) => {
      this.gameListener.onScoreChange(payload.value);
    });
    nextRound.gameLogicActions.on('onRoundStateChanged', (payload) => {
      this.gameListener.onRoundStateChanged(payload.state);
    });
    nextRound.gameLogicActions.on('onPlayerControlsChanged', (payload) => {
      this.gameListener.onPlayerControlsChanged(payload.controls);
    });
    nextRound.gameLogicActions.on('onFatalError', (payload) => {
      this.gameListener.onFatalError(payload.context, payload.error);
    });

    this.gameRound = nextRound;
    this.blackjackLogicActionBus.next(nextRound.gameLogicActions);
    this.logicEventQueue.next(nextRound.logicEventQueue);
    return nextRound;
  }

  public override createRemoteRoundParams(
    gameListener: BlackjackListener,
    logicVersion: number,
  ): RoundParams {
    return BlackjackRemoteRoundParams.create(gameListener, logicVersion);
  }

  public override createLocalRoundParams(gameListener: BlackjackListener): RoundParams {
    return BlackjackLocalRoundParams.create(gameListener);
  }

  private createRemoteRound(remoteRoundParams: BlackjackRemoteRoundParams): BlackjackGameRound {
    const gameLogic = BlackjackRoundCreator.createGame(
      remoteRoundParams.dealer,
      remoteRoundParams.logicVersion,
    );

    return new BlackjackGameRound(gameLogic, remoteRoundParams);
  }

  private createLocalRound(localRoundParams: BlackjackLocalRoundParams): BlackjackGameRound {
    const gameLogic = BlackjackRoundCreator.createGame(localRoundParams.dealer);

    return new BlackjackGameRound(gameLogic, localRoundParams);
  }

  private destroyCurrentGame(): void {
    if (this.gameRound) {
      this.blackjackLogicActionBus.next(null);
      this.logicEventQueue.next(null);
    }
  }

  private static createGame(
    dealer: BlackjackDealer,
    logicVersion = BlackjackRoundCreator.defaultLogicVersion,
  ): BlackjackGameLogic {
    return BlackjackGameBuilder.newGame(dealer, logicVersion);
  }
}
